import { Cover } from '../primitives';
import { localizedField } from '../../locales/content';
import { hapticImpact } from '../../utils/haptics';
import TitleImageOverlay from './TitleImageOverlay';

/**
 * PresetGroupCardPacks — preset-группа с displayMode='shuffle'.
 *
 * Strict-audit §9 findings (REJECT of prior implementation):
 *   Figma variant `type=packs` (123:2913) НЕ 2×2 collage. Это такой же
 *   single-image cover, как viral/lux/hot — разница только в image-ref-е.
 *   Предыдущая реализация с `grid grid-cols-2 grid-rows-2 gap-[2px]` —
 *   INVENTED.
 *
 * Reconciliation (strict-audit § «Proposed fix workflow» item 7):
 *   displayMode='shuffle' теперь означает: backend уже подготовил
 *   pre-cooked image (например из backend `previewSlideshowUrls`), где
 *   4 мини-фото уже запечены в одну картинку. Frontend показывает её как
 *   обычный cover.
 *
 * Fallback: если `group.cover` отсутствует, но есть 1+ preset с image —
 *   используем image первого пресета. Без тестовых data-role='pack-tile'
 *   меток (их scheme был invented).
 *
 * Closures (strict-audit §9, 2 CRITICAL + 2 MAJOR + 1 INVENTED):
 *   1. rounded-home-16 → rounded-home-24 (via Cover primitive).
 *   2. REMOVED invented 2×2 grid layout (Figma packs = single image).
 *   3. REMOVED invented bottom-gradient.
 *   4. REMOVED invented text-fallback.
 *   5. NewBadge top-2 left-2 → top-0 right-0 (via Cover primitive).
 *
 * Props:
 *   group   — { id, name, cover?, presets?: [{id,image}], titleImage?, isNew? }
 *   onClick — (group) => void
 */
export default function PresetGroupCardPacks({ group, onClick, fluid = false }) {
  if (!group) return null;

  const { cover, coverThumb, titleImage, isNew, presets = [] } = group;
  const name = localizedField(group, 'name') || group.name;

  // If backend hasn't cooked a dedicated cover for this shuffle group,
  // fall back to the first preset image. Neither Figma nor admin-data
  // contract specifies a 2×2 composite here.
  const coverUrl = coverThumb || cover || presets[0]?.homeThumbUrl || presets[0]?.image || null;

  return (
    <button
      type="button"
      onClick={() => {
        hapticImpact('light');
        if (onClick) onClick(group);
      }}
      className={[
        'relative flex-shrink-0',
        fluid ? 'aspect-square w-full' : '',
        'text-left',
        'p-0 border-0 bg-transparent',
      ].join(' ')}
      aria-label={name}
    >
      <Cover
        coverUrl={coverUrl}
        alt={name || ''}
        isNew={isNew}
        style={fluid ? { width: '100%', height: '100%' } : undefined}
      />

      {/* Optional title PNG overlay — same contract as PresetGroupCard. */}
      <TitleImageOverlay src={titleImage} group={group} />
    </button>
  );
}
